import { PublicAxiosInstance } from '@/api/axios'
import { logoImages } from '@/assets/images'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FullPageSkeleton } from '@/components/ui/skeleton'
import { SignatureVerificationLevel } from '@/enum/global.enum'
import { useDateFormat } from '@/hooks/use-date-format'
import { setPageTitle } from '@/hooks/use-page-title'
import { useTheme } from '@/hooks/use-theme'
import type { IPublicSignatureVerification } from '@/interface/signature.interface'
import { cn } from '@/lib/utils'
import { signatureIntentLabel } from '@/utils/signature.utils'
import type { AxiosError } from 'axios'
import { Check, Hash, Home, ShieldAlert, ShieldCheck, User, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'

/** Stated as what was actually established, not as proof of identity. */
const VERIFICATION_LABEL: Record<SignatureVerificationLevel, string> = {
  EMAIL_LINK: 'Control of a unique signing link sent to the signer’s email address',
  EMAIL_OTP: 'Control of the signer’s email address, confirmed by a one-time code at signing time',
  AUTHENTICATED: 'An authenticated platform session belonging to the signer',
  SSO: 'A single sign-on session established by the organisation’s identity provider',
}

const CheckRow = ({ label, valid, reason }: { label: string; valid: boolean; reason?: string }) => (
  <div className='flex items-start gap-2 rounded  '>
    <div
      className={cn(
        'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
        valid ? 'bg-green-600/5 text-green-600 dark:text-green-400' : 'bg-destructive/5 text-destructive'
      )}
    >
      {valid ? <Check className='h-3 w-3' /> : <X className='h-3 w-3' />}
    </div>
    <div className='min-w-0 flex-1 pt-[0.5px]'>
      <p className='text-sm'>{label}</p>
      {!valid && reason ? <p className='mt-0.5 text-xs text-destructive'>{reason}</p> : null}
    </div>
  </div>
)

const DetailItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className='flex justify-between gap-4 text-sm'>
    <span className='shrink-0 text-muted-foreground'>{label}</span>
    <span className='break-all text-right'>{value}</span>
  </div>
)

/**
 * Public signature verification.
 *
 * Reached from the QR code on a Certificate of Signature, so it must work with no
 * account. It shows whether the signature holds and who made it, and never the
 * document contents - anyone holding the certificate can open this page.
 */
const SignatureVerificationPage = () => {
  const { id } = useParams()
  const { theme } = useTheme()
  const { formatDate } = useDateFormat()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<IPublicSignatureVerification | null>(null)

  useEffect(() => {
    const verify = async () => {
      if (!id) {
        setError('No signature reference provided')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await PublicAxiosInstance.get(`/signature/verify/${id}`)
        setResult(response.data.data as IPublicSignatureVerification)
      } catch (err) {
        setError(
          (err as AxiosError<{ message: string }>)?.response?.data?.message ||
            'This signature could not be found or verified'
        )
      } finally {
        setLoading(false)
      }
    }

    void verify()
  }, [id])

  void setPageTitle('Signature Verification')

  if (loading) return <FullPageSkeleton />

  if (error || !result)
    return (
      <div className='flex min-h-screen flex-col'>
        <div className='flex-shrink-0 border-b p-4'>
          <div className='mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3'>
            <div className='flex flex-col items-start gap-3'>
              <img
                loading='lazy'
                decoding='async'
                src={theme === 'dark' ? logoImages.logoLight : logoImages.logoBlack}
                alt='CISO Adapt'
                className='h-8 w-auto'
              />
              <div>
                <h1 className='text-xl font-bold'>Signature Verification</h1>
                <div className='mt-1 flex flex-wrap items-center gap-2'>
                  <Badge className='bg-destructive/10 text-destructive hover:bg-destructive/10'>
                    <ShieldAlert className='mr-1 h-3 w-3' />
                    Signature not verified
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='flex-1 overflow-y-auto p-4'>
          <div className='mx-auto max-w-4xl space-y-4'>
            <div className='rounded-md border p-4'>
              <div className='flex items-start gap-3'>
                <ShieldAlert className='mt-0.5 h-5 w-5 shrink-0 text-destructive' />
                <div className='min-w-0 flex-1'>
                  <h2 className='text-md font-semibold'>Signature not verified</h2>
                  <p className='mt-1 text-sm text-muted-foreground'>
                    {error ?? 'This signature could not be verified'}
                  </p>
                  <Button className='mt-4' onClick={() => (window.location.href = '/')} size={'sm'}>
                    <Home className='h-4 w-4' />
                    Go Home
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )

  const statusLabel = result.valid ? 'Signature verified' : result.revoked ? 'Signature revoked' : 'Signature not valid'

  const statusDescription = result.valid
    ? 'This signature is authentic and the signed document has not changed.'
    : (result.revokedReason ??
      result.documentIntegrity.reason ??
      result.evidenceSeal.reason ??
      result.chain.reason ??
      'One or more verification checks did not pass.')

  return (
    <div className='flex min-h-screen flex-col'>
      {/* Fixed Header - matches public policy review pages */}
      <div className='flex-shrink-0 border-b p-4'>
        <div className='mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3'>
          <div className='flex flex-col items-start gap-3'>
            <img
              loading='lazy'
              decoding='async'
              src={theme === 'dark' ? logoImages.logoLight : logoImages.logoBlack}
              alt='CISO Adapt'
              className='h-8 w-auto'
            />
            <div>
              <h1 className='text-xl font-bold'>Signature Verification</h1>
              <div className='mt-1 flex flex-wrap items-center gap-2'>
                <Badge
                  className={cn(
                    result.valid
                      ? 'bg-green-600/10 text-green-700 hover:bg-green-600/10 dark:text-green-400'
                      : 'bg-destructive/10 text-destructive hover:bg-destructive/10'
                  )}
                >
                  {result.valid ? <ShieldCheck className='mr-1 h-3 w-3' /> : <ShieldAlert className='mr-1 h-3 w-3' />}
                  {statusLabel}
                </Badge>
                {result.documentName ? (
                  <span className='text-sm text-muted-foreground'>{result.documentName}</span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className='flex-1 overflow-y-auto p-4'>
        <div className='mx-auto max-w-4xl space-y-4'>
          {/* Status summary */}
          <div className='rounded-md border p-4'>
            <div className={cn('flex items-start gap-3')}>
              {result.valid ? (
                <ShieldCheck className='mt-[3px] h-5 w-5 shrink-0 text-green-600 dark:text-green-400' />
              ) : (
                <ShieldAlert className='mt-[3px] h-5 w-5 shrink-0 text-destructive' />
              )}
              <div>
                <h2 className='text-md font-semibold'>{statusLabel}</h2>
                <p className='mt-1 text-sm text-muted-foreground'>{statusDescription}</p>
              </div>
            </div>
          </div>

          {/* Signer & document details */}
          <div className='rounded-md border p-4'>
            <h2 className='mb-3 flex items-center gap-1 text-md font-semibold'>
              <User className='mb-[2px] h-4 w-4' />
              Signature Details
            </h2>
            <div className='grid grid-cols-1 gap-3'>
              <DetailItem label='Signed by' value={`${result.signer.name} (${result.signer.email})`} />
              {result.signer.role ? <DetailItem label='Role' value={result.signer.role} /> : null}
              {result.organisationName ? (
                <DetailItem
                  label='Organisation'
                  value={<span className='inline-flex items-center gap-1'>{result.organisationName}</span>}
                />
              ) : null}
              {result.documentName ? (
                <DetailItem
                  label='Document'
                  value={<span className='inline-flex items-center gap-1'>{result.documentName}</span>}
                />
              ) : null}
              <DetailItem
                label='Intent'
                value={<Badge variant='outline'>{signatureIntentLabel[result.intent]}</Badge>}
              />
              <DetailItem label='Signed at' value={formatDate(result.signedAt)} />
              <DetailItem
                label='Document version hash'
                value={
                  <span className='inline-flex max-w-[220px] items-start gap-1 font-mono text-xs sm:max-w-none'>
                    <Hash className='mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground' />
                    {result.documentHash}
                  </span>
                }
              />
            </div>

            <div className='mt-4 rounded border-t pt-3'>
              <p className='text-xs font-medium text-muted-foreground'>Identity evidence</p>
              <p className='mt-1 text-sm'>{VERIFICATION_LABEL[result.verificationLevel]}</p>
            </div>
          </div>

          {/* Verification checks */}
          <div className='rounded-md border p-4'>
            <h2 className='mb-3 flex items-center gap-1 text-md font-semibold'>
              <ShieldCheck className='mb-[2px] h-4 w-4' />
              Verification Checks
            </h2>
            <div className='space-y-3'>
              <CheckRow
                label='The document is unchanged since it was signed'
                valid={result.documentIntegrity.valid}
                reason={result.documentIntegrity.reason}
              />
              <CheckRow
                label={`The cryptographic seal is valid (${result.evidenceSeal.algorithm})`}
                valid={result.evidenceSeal.valid}
                reason={result.evidenceSeal.reason}
              />
              <CheckRow label='The signature chain is intact' valid={result.chain.valid} reason={result.chain.reason} />
              <CheckRow
                label='The signature has not been revoked'
                valid={!result.revoked}
                reason={result.revokedReason ?? undefined}
              />
            </div>
          </div>

          {/* Legal / scope note */}
          <div className='rounded-md border p-4'>
            <h2 className='mb-2 text-md font-semibold'>What this confirms</h2>
            <p className='text-xs text-muted-foreground'>
              Verification recomputes the hash of the stored document and compares it with the hash recorded at signing
              time, checks the cryptographic seal applied with the organisation’s private signing key, and confirms the
              signature’s position in the append-only signature chain.
            </p>
            <p className='mt-2 text-xs text-muted-foreground'>
              It does not attest to the verification of any government-issued identity document. The identity evidence
              relied on is stated above. This is a simple electronic signature; it is not a qualified electronic
              signature within the meaning of EU Regulation 910/2014 (eIDAS).
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignatureVerificationPage
